"use client";

import type { PushSubscriptionChangedState } from "@onesignal/capacitor-plugin";
import { getAppRouteFromUrl } from "@/lib/client/appUrl";
import { isNativeCapacitorApp } from "@/lib/client/capacitor";
import { fetchNotifications, markNotificationRead } from "@/lib/client/notifications";

type OneSignalNativeModule = typeof import("@onesignal/capacitor-plugin");
type OneSignalNativeSdk = OneSignalNativeModule["default"];
type NativeSubscriptionListener = (optedIn: boolean) => void;

type NativePushMutationResult = {
  ok: boolean;
  optedIn: boolean;
  error?: string;
};

let nativeModulePromise: Promise<OneSignalNativeModule | null> | null = null;
let nativeInitialized = false;
let nativeInitializationPromise: Promise<void> | null = null;
let nativeClickListenerAttached = false;

const PUSH_STATE_POLL_INTERVAL_MS = 250;
const PUSH_STATE_POLL_ATTEMPTS = 8;
const DEFAULT_ONESIGNAL_APP_ID = "783ec07d-4580-4a95-aabb-0fa0a6f3f26b";

export const getOneSignalAppId = () =>
  process.env.NEXT_PUBLIC_ONESIGNAL_APP_ID?.trim() ||
  process.env.NEXT_PUBLIC_ONESIGNAL_APP_KEY?.trim() ||
  DEFAULT_ONESIGNAL_APP_ID;

export const getOneSignalSafariWebId = () =>
  process.env.NEXT_PUBLIC_ONESIGNAL_SAFARI_KEY?.trim() ||
  "web.onesignal.auto.6836a56b-23bd-4172-b38f-daf17d70b8cc";

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const normalizeNotificationTarget = (value: string | null | undefined) => {
  if (!value || typeof window === "undefined") {
    return null;
  }

  try {
    const resolved = new URL(value, window.location.origin);

    if (resolved.origin === window.location.origin) {
      return `${resolved.pathname}${resolved.search}${resolved.hash}`;
    }

    return resolved.toString();
  } catch {
    return value;
  }
};

export const getNativeNotificationRoute = (targetUrl: string | null) => {
  if (!targetUrl) {
    return null;
  }

  if (targetUrl.startsWith("/")) {
    return targetUrl;
  }

  return getAppRouteFromUrl(targetUrl);
};

const extractString = (value: unknown) => {
  return typeof value === "string" && value.length > 0 ? value : null;
};

const resolveNotificationTarget = (
  notification: { launchURL?: string; additionalData?: object },
  clickUrl?: string
) => {
  const additionalData = notification.additionalData as Record<string, unknown> | undefined;

  return normalizeNotificationTarget(
    extractString(clickUrl) ||
      extractString(notification.launchURL) ||
      extractString(additionalData?.url) ||
      extractString(additionalData?.targetUrl) ||
      extractString(additionalData?.path)
  );
};

const markMatchingNotificationRead = async (targetUrl: string | null) => {
  if (!targetUrl) {
    return;
  }

  try {
    const notifications = await fetchNotifications(50, 0);
    const normalizedTarget = normalizeNotificationTarget(targetUrl);
    const match = notifications.find((notification) => {
      return (
        !notification.is_read && normalizeNotificationTarget(notification.url) === normalizedTarget
      );
    });

    if (match) {
      await markNotificationRead(match.id);
    }
  } catch (error) {
    console.error("Failed to mark native notification as read:", error);
  }
};

const navigateToNotificationTarget = (targetUrl: string | null) => {
  if (!targetUrl || typeof window === "undefined") {
    return;
  }

  if (isNativeCapacitorApp()) {
    const appRoute = getNativeNotificationRoute(targetUrl);

    if (appRoute) {
      window.location.assign(appRoute);
    }

    return;
  }

  window.location.assign(targetUrl);
};

const loadNativeModule = async () => {
  if (!isNativeCapacitorApp()) {
    return null;
  }

  if (!nativeModulePromise) {
    nativeModulePromise = import("@onesignal/capacitor-plugin").catch((error) => {
      console.error("Failed to load the native OneSignal SDK:", error);
      return null;
    });
  }

  return nativeModulePromise;
};

const waitForNativeOptInState = async (sdk: OneSignalNativeSdk, expectedState?: boolean) => {
  for (let attempt = 0; attempt < PUSH_STATE_POLL_ATTEMPTS; attempt += 1) {
    const optedIn = await sdk.User.pushSubscription.getOptedInAsync();

    if (expectedState === undefined || optedIn === expectedState) {
      return optedIn;
    }

    await wait(PUSH_STATE_POLL_INTERVAL_MS);
  }

  return sdk.User.pushSubscription.getOptedInAsync();
};

const waitForNativeExternalId = async (sdk: OneSignalNativeSdk, expectedUserId: string) => {
  for (let attempt = 0; attempt < PUSH_STATE_POLL_ATTEMPTS; attempt += 1) {
    if ((await sdk.User.getExternalId()) === expectedUserId) {
      return true;
    }

    await wait(PUSH_STATE_POLL_INTERVAL_MS);
  }

  return (await sdk.User.getExternalId()) === expectedUserId;
};

export const syncNativeOneSignalUser = async (sdk: OneSignalNativeSdk, userId?: string | null) => {
  // An omitted ID means this is a read-only SDK access. Do not change the
  // current OneSignal user when reading the native subscription state.
  if (userId === undefined) {
    return true;
  }

  try {
    const currentExternalId = await sdk.User.getExternalId();

    if (userId === null) {
      if (currentExternalId) {
        await sdk.logout();
      }

      return true;
    }

    if (currentExternalId === userId) {
      return true;
    }

    await sdk.login(userId);
    const synced = await waitForNativeExternalId(sdk, userId);

    if (!synced) {
      console.error("Native OneSignal External ID did not synchronize", {
        expectedUserId: userId,
      });
    }

    return synced;
  } catch (error) {
    console.error("Failed to associate the native OneSignal subscription with the user:", error);
    return false;
  }
};

export const initializeNativeOneSignal = async (userId?: string | null) => {
  const nativeModule = await loadNativeModule();
  if (!nativeModule) {
    return null;
  }

  const sdk = nativeModule.default;
  const appId = getOneSignalAppId();

  if (!appId) {
    console.error("Native OneSignal initialization skipped because no app ID is configured.");
    return null;
  }

  if (!nativeInitialized) {
    if (process.env.NODE_ENV !== "production") {
      sdk.Debug.setLogLevel(nativeModule.LogLevel.Debug);
    }

    if (!nativeInitializationPromise) {
      nativeInitializationPromise = sdk
        .initialize(appId)
        .then(() => {
          nativeInitialized = true;
        })
        .catch((error) => {
          nativeInitializationPromise = null;
          throw error;
        });
    }

    await nativeInitializationPromise;
  }

  if (!nativeClickListenerAttached) {
    sdk.Notifications.addEventListener("click", async (event) => {
      const targetUrl = resolveNotificationTarget(event.notification, event.result?.url);
      await markMatchingNotificationRead(targetUrl);
      navigateToNotificationTarget(targetUrl);
    });

    nativeClickListenerAttached = true;
  }

  if (userId !== undefined && !(await syncNativeOneSignalUser(sdk, userId))) {
    throw new Error("Could not link native OneSignal notifications to this account.");
  }

  return sdk;
};

export const getNativePushOptedIn = async () => {
  const sdk = await initializeNativeOneSignal();
  if (!sdk) {
    return false;
  }

  return sdk.User.pushSubscription.getOptedInAsync();
};

export const getNativePushSubscriptionId = async () => {
  const sdk = await initializeNativeOneSignal();
  if (!sdk) {
    return null;
  }

  return sdk.User.pushSubscription.getIdAsync();
};

export const addNativePushSubscriptionObserver = async (listener: NativeSubscriptionListener) => {
  const sdk = await initializeNativeOneSignal();
  if (!sdk) {
    return () => undefined;
  }

  const handleChange = (event: PushSubscriptionChangedState) => {
    listener(Boolean(event.current.optedIn));
  };

  sdk.User.pushSubscription.addEventListener("change", handleChange);

  return () => {
    sdk.User.pushSubscription.removeEventListener("change", handleChange);
  };
};

export const enableNativePushNotifications = async (
  userId: string
): Promise<NativePushMutationResult> => {
  const sdk = await initializeNativeOneSignal(userId);
  if (!sdk) {
    return {
      ok: false,
      optedIn: false,
      error: "NATIVE NOTIFICATIONS ARE NOT AVAILABLE.",
    };
  }

  const canRequestPermission = await sdk.Notifications.canRequestPermission();
  if (canRequestPermission) {
    const granted = await sdk.Notifications.requestPermission(true);

    if (!granted) {
      return {
        ok: false,
        optedIn: false,
        error: "NOTIFICATION PERMISSION WAS NOT GRANTED",
      };
    }
  }

  sdk.User.pushSubscription.optIn();
  const optedIn = await waitForNativeOptInState(sdk, true);

  if (!optedIn) {
    return {
      ok: false,
      optedIn: false,
      error: "NOTIFICATION PERMISSION WAS NOT GRANTED",
    };
  }

  return {
    ok: true,
    optedIn: true,
  };
};

export const disableNativePushNotifications = async (): Promise<NativePushMutationResult> => {
  const sdk = await initializeNativeOneSignal();
  if (!sdk) {
    return {
      ok: false,
      optedIn: false,
      error: "NATIVE NOTIFICATIONS ARE NOT AVAILABLE.",
    };
  }

  sdk.User.pushSubscription.optOut();
  const optedIn = await waitForNativeOptInState(sdk, false);

  return {
    ok: !optedIn,
    optedIn,
    ...(optedIn ? { error: "SOMETHING WENT WRONG. PLEASE TRY AGAIN LATER" } : {}),
  };
};
